import React, { LegacyRef, MutableRefObject, useRef, useState } from 'react'
import { Box, Button, DialogProps, IconButton, Typography } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'
import DialogContent from '@mui/material/DialogContent'
import logoVermelho from 'assets/logo-mac-cor.png'
import { styled } from '@mui/material/styles'
import dayjs from 'dayjs'
import { formatAddressFieldV2, formatText } from 'utils/text.util'
import DialogActions from '@mui/material/DialogActions'
import { FamiliarReferenceV2Type } from 'types/familiar-reference-v2.type'
import { useInputSelectProceduralData } from 'pages/CreateHearing/components/StepProceduralData/hooks/select-option.hook'
import {
  medidaAplicadaOptions,
  medidaRestritivaOptions,
  tipoRemissaoOptions,
} from 'pages/SocioeducationMeasureEdit/ProceduralData'
import {
  MeasureStatus,
  MeasureStatusLaPsc,
  SocioeducationMeasureProceduralDataType,
} from 'types/socioeducation-measure-procedural-data.type'
import {
  MeasuresAppliedType,
  SocioeducationMeasureMeasuresAppliedType,
} from 'types/socioeducation-measure-measures-applied.type'
import {
  atividadesOptions,
  dificuldadeAdesaoOptions,
  periodicidadeAcompanhamentoOptions,
  simNaoOptions,
  sugestaoExtincaoOptions,
} from 'pages/SocioeducationMeasureEdit/MeasuresApplied'
import {
  motivosNaoAdesaoOptions,
  motivosOptions,
  trabalhoOptions,
} from 'pages/SocioeducationMeasureEdit/components/SchoolingLearningWorkProcess'
import {
  acompSaudeMentalOptions,
  relacaoAdolescenteDrogaOptions,
} from 'pages/SocioeducationMeasureEdit/components/ComprehensiveHealth'
import { familiaAcompanhaOutroServicoOptions } from 'pages/SocioeducationMeasureEdit/components/SocialAssistanceNetwork'
import { useInputSelectIdentification } from 'pages/CreateHearing/components/StepIdentification/hooks/select-option.hook'
import { useReactToPrint } from 'react-to-print'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import { StyledPrintContent } from 'pages/SocioeducationMeasureEdit/GenerateReport/style'
import {
  culturalOptions,
  sportsOptions,
} from 'pages/SocioeducationMeasureEdit/components/CultureSports'
import { substances } from 'types/hearing-identification.type'
import { SocioeducationMeasureIdentificationType } from 'types/socioeducation-measure-identification.type'
import { SocioeducationMeasureFamilyDataType } from 'types/socioeducation-measure-family-data.type'
import { SocioeducationAccompanimentDataType } from 'types/socioeducation-accompaniment-data.type'

const logoMinisterioPublico = '/img/logotipo_MP.png'

const StyledImgSistema = styled('img')(() => ({
  height: '4.25rem',
}))

const StyledImgMprj = styled('img')(() => ({
  height: '2.5rem',
}))

const getAge = (date?: string | null) => {
  const today = dayjs()
  const birth = dayjs(date)
  const age = today.diff(birth, 'year')

  return age.toString()
}

const getBirthDate = (date?: string | null) => {
  if (!date) return null

  return formatDate(date)
}

const formatDate = (date?: string | null) => {
  if (!date) return ''
  return dayjs(date).format('DD/MM/YYYY')
}

const getSimNaoDescricao = (value?: boolean | string | null, descricao?: string | null) => {
  if (typeof value === 'boolean') {
    return value ? `Sim - ${descricao || ''}` : 'Não'
  }

  if (value === 'S') {
    return `Sim - ${descricao || ''}`
  }

  if (value === 'N') {
    return 'Não'
  }

  return ''
}

const isSimNao = (value?: boolean | string | null) => {
  if (typeof value === 'boolean') {
    return value
  }

  return value === 'S'
}

interface SocioeducationMeasureProps {
  identificacao?: SocioeducationMeasureIdentificationType | null
  dadosFamiliares?: SocioeducationMeasureFamilyDataType | null
  dadosProcessuais?: SocioeducationMeasureProceduralDataType | null
  medidasAplicadas?: SocioeducationMeasureMeasuresAppliedType | null
  dadosAcompanhamento?: SocioeducationAccompanimentDataType | null
}

interface ModalSocioeducationMeasureReportProps extends SocioeducationMeasureProps {
  open: boolean
  onClose: () => void
}

export const ModalSocioeducationMeasureReport = ({
  open,
  onClose,
  dadosAcompanhamento,
  dadosProcessuais,
  medidasAplicadas,
  dadosFamiliares,
  identificacao,
}: ModalSocioeducationMeasureReportProps) => {
  const [fullWidth] = useState(true)
  const [maxWidth] = useState<DialogProps['maxWidth']>('md')

  const componentRef = useRef(null)

  const handlePrint = useReactToPrint({
    content: () => componentRef?.current,
    documentTitle: '',
  })

  return (
    <Dialog open={open} onClose={onClose} fullWidth={fullWidth} maxWidth={maxWidth}>
      <DialogTitle>
        <Typography variant={'h6'} color={'primary.main'} />
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <SocioeducationMeasureReport
          componentRef={componentRef}
          medidasAplicadas={medidasAplicadas}
          identificacao={identificacao}
          dadosFamiliares={dadosFamiliares}
          dadosAcompanhamento={dadosAcompanhamento}
          dadosProcessuais={dadosProcessuais}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button variant={'text'} color={'secondary'} size={'large'} onClick={onClose}>
          Fechar
        </Button>
        <Button
          variant='contained'
          color='info'
          startIcon={<PrintRoundedIcon />}
          size='large'
          sx={{ marginRight: '1rem' }}
          onClick={handlePrint}
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface SocioeducationMeasureReportProps extends SocioeducationMeasureProps {
  componentRef: MutableRefObject<any>
}

export const SocioeducationMeasureReport = ({
  medidasAplicadas,
  dadosFamiliares,
  identificacao,
  dadosAcompanhamento,
  dadosProcessuais,
  componentRef,
}: SocioeducationMeasureReportProps) => (
  <StyledPrintContent ref={componentRef as LegacyRef<HTMLDivElement>}>
    <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} gap={4}>
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'center'}
        alignItems={'center'}
        gap={6}
      >
        <StyledImgMprj src={logoMinisterioPublico} alt={'ministerio_publico'} />
        <StyledImgSistema src={logoVermelho} alt={'logo_sistema'} />
      </Box>

      <h3 style={{ textAlign: 'center' }}>
        FORMULÁRIO PARA ELABORAÇÃO DO PLANO INDIVIDUAL DE ATENDIMENTO - PIA
      </h3>
      <IdentificacaoSection identificacao={identificacao} />
      <FamilyDataSection dadosFamiliares={dadosFamiliares} />
      <ProceduralDataSection dadosProcessuais={dadosProcessuais} />
      <MeasuresAppliedSection medidasAplicadas={medidasAplicadas} />
      <AccompanimentSection dadosAcompanhamento={dadosAcompanhamento} />
    </Box>
  </StyledPrintContent>
)

interface LabelProps {
  label: string
  value?: string | number | null
  direction?: 'row' | 'column'
}

const Label = ({ label, value, direction = 'row' }: LabelProps) => (
  <Box display={'flex'} flexDirection={direction} gap={1}>
    <Typography variant={'body2'}>
      <b>{label}</b>
    </Typography>
    <Typography variant={'body2'}>{value}</Typography>
  </Box>
)

const IdentificacaoSection = ({
  identificacao,
}: {
  identificacao?: SocioeducationMeasureIdentificationType | null
}) => {
  const { genderOptions, skinOptions } = useInputSelectIdentification()

  const isEnderecoPrincipal = (endereco?: any) => {
    if (endereco.enderecoPrincipal === 'S') return 'Sim'
    return 'Não'
  }

  const getGenero = (value?: number | null | string) => {
    if (!value) return ''
    return genderOptions.find((item) => item.value === value)?.label
  }

  const getCor = (value?: number | null | string) => {
    if (!value) return ''
    return skinOptions.find((item) => item.value === value)?.label
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Typography variant={'h6'} sx={{ borderBottom: '1px solid', borderColor: '#000' }}>
        <b>IDENTIFICAÇÃO</b>
      </Typography>
      <Typography variant={'body1'}>
        <b>Identificação do adolescente/jovem:</b>
      </Typography>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Label label={'Nome social:'} value={identificacao?.pessoaFisica.nomeSocial} />
        <Label label={'Nome:'} value={identificacao?.pessoaFisica.nome} />
        <Label
          label={'Data de nascimento:'}
          value={getBirthDate(identificacao?.pessoaFisica.dataNascimento)}
        />
        <Label label={'Idade:'} value={getAge(identificacao?.pessoaFisica.dataNascimento)} />
        <Label label={'Gênero:'} value={getGenero(identificacao?.pessoaFisica.genero?.id)} />
        <Label label={'Cor:'} value={getCor(identificacao?.pessoaFisica.corPele?.id)} />
        <Label label={'Nome da mãe:'} value={identificacao?.pessoaFisica.nomeMae} />
        <Label label={'Nome do pai:'} value={identificacao?.pessoaFisica.nomePai} />
        <Label label={'Responsável legal:'} value={identificacao?.nomeResponsavelLegal} />
        <Label label={'Religião:'} value={identificacao?.religiao} />
        <Label label={'CREAS:'} value={identificacao?.creas?.nome} />
      </Box>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Typography variant={'body1'}>
          <b>Documentos:</b>
        </Typography>
        {identificacao?.pessoaFisica.documentos?.map((documento) => (
          <Label
            key={documento.descricaoDocumento}
            label={`${documento.tipoDocumento.descricao || documento.tipoDocumento.nome}:`}
            value={documento.descricaoDocumento}
          />
        ))}
      </Box>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Typography variant={'body1'}>
          <b>Endereços:</b>
        </Typography>
        {identificacao?.pessoaFisica.enderecos?.map((endereco, index) => (
          <div key={index}>
            <Label label={'Endereço principal:'} value={isEnderecoPrincipal(endereco)} />
            <Typography variant={'body2'}>{formatAddressFieldV2(endereco.endereco)}</Typography>
          </div>
        ))}
      </Box>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Typography variant={'body1'}>
          <b>Telefones:</b>
        </Typography>
        {identificacao?.pessoaFisica.telefones?.map((telefone) => (
          <Label
            key={telefone.descricao}
            label={`${telefone.descricao}:`}
            value={telefone.telefone}
          />
        ))}
      </Box>
    </Box>
  )
}

const FamilyDataSection = ({
  dadosFamiliares,
}: {
  dadosFamiliares?: SocioeducationMeasureFamilyDataType | null
}) => {
  const FamiliarComposition = ({
    referencesFamiliar,
  }: {
    referencesFamiliar: FamiliarReferenceV2Type[]
  }) => (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      {referencesFamiliar.map((referenceFamiliar, index) => (
        <Box key={index} display={'flex'} flexDirection={'column'} gap={1}>
          <Label
            label={'Grau de parentesco:'}
            value={referenceFamiliar?.tipoReferencia.descricao}
          />
          <Label label={'Nome:'} value={referenceFamiliar?.pessoaReferencia.nome} />
          <Label
            label={'Data de nascimento:'}
            value={getBirthDate(referenceFamiliar?.pessoaReferencia.dataNascimento)}
          />
          <Label label={'Profissão/ocupação:'} value={referenceFamiliar?.descricaoProfissao} />
          <Label label={'Renda/benefício:'} value={referenceFamiliar?.descricaoRendaBeneficio} />
          <Label label={'Telefone:'} value={referenceFamiliar?.telefone} />
        </Box>
      ))}
    </Box>
  )

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Typography variant={'h6'} sx={{ borderBottom: '1px solid', borderColor: '#000' }}>
        <b>DADOS FAMILIARES</b>
      </Typography>
      <Typography variant={'body1'}>
        <b>Composição familiar:</b>
      </Typography>
      <FamiliarComposition referencesFamiliar={dadosFamiliares?.referenciasFamiliares || []} />
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Typography variant={'subtitle2'}>
          <b>
            Relações familiares e comunitárias (participação da família no cumprimento da medida,
            potencialidades, vulnerabilidades, violências, riscos e conflitos):
          </b>
        </Typography>
        <Typography variant={'body2'}>{dadosFamiliares?.descricaoRelacoesFamiliares}</Typography>
      </Box>
      <Typography variant={'body1'}>
        <b>Outras referências familiares, afetivas e institucionais:</b>
      </Typography>
      <FamiliarComposition
        referencesFamiliar={dadosFamiliares?.outrasReferenciasFamiliares || []}
      />
    </Box>
  )
}

const ProceduralDataSection = ({
  dadosProcessuais,
}: {
  dadosProcessuais?: SocioeducationMeasureProceduralDataType | null
}) => {
  const { measureStatusLaPscOptions, measureStatusOptions } = useInputSelectProceduralData()

  const municipioDeclarado =
    dadosProcessuais?.medidaSocioeducativa.la?.estadoMoradiaDeclarado ||
    dadosProcessuais?.medidaSocioeducativa.psc?.estadoMoradiaDeclarado ||
    dadosProcessuais?.medidaSocioeducativa.laPsc?.estadoMoradiaDeclarado

  const estadoDeclarado =
    dadosProcessuais?.medidaSocioeducativa.la?.estadoMoradiaDeclarado ||
    dadosProcessuais?.medidaSocioeducativa.psc?.estadoMoradiaDeclarado ||
    dadosProcessuais?.medidaSocioeducativa.laPsc?.estadoMoradiaDeclarado

  const getRemissao = (trataRemissao?: boolean | null, tipoRemissao?: string | null) => {
    if (trataRemissao) {
      const tipoRemissaoDescricao = tipoRemissaoOptions.find((item) => item.value === tipoRemissao)
      return `Sim - ${tipoRemissaoDescricao?.label}`
    }

    return 'Não'
  }

  const getMedidaAplicada = (medidaAplicada?: string | null) => {
    if (medidaAplicada) {
      return medidaAplicadaOptions.find((item) => item.value === medidaAplicada)?.label
    }

    return ''
  }

  const getStatusMedida = (statusMedida?: string | null, statusMedidaLaPsc?: string | null) => {
    if (statusMedida || statusMedidaLaPsc) {
      let statusOption = measureStatusOptions.find((item) => item.value === statusMedida)
      if (!statusOption) {
        statusOption = measureStatusLaPscOptions.find((item) => item.value === statusMedidaLaPsc)
      }
      return statusOption?.label
    }

    return ''
  }

  const getMedidaRestritiva = (cumprimentoMedida?: string | null) => {
    if (cumprimentoMedida) {
      return medidaRestritivaOptions.find((item) => item.value === cumprimentoMedida)?.label
    }

    return ''
  }

  const getDateLabel = (statusMedida?: string | null, statusMedidaLaPsc?: string | null) => {
    const labelMap: Record<MeasureStatus | MeasureStatusLaPsc, string> = {
      [MeasureStatus.CUMPRINDO]: '',
      [MeasureStatus.DESCUMPRINDO]: 'Data do Início do Desligamento por Descumprimento',
      [MeasureStatus.EXTINTA]: 'Data do Desligamento por Sentença de Extinção',
      [MeasureStatus.OBITO]: 'Data do Desligamento por Óbito',
      [MeasureStatus.SUSPENSA]: 'Data da Sentença',
      [MeasureStatus.TRANSFERENCIA_CREAS]: 'Data da Transferência',
      [MeasureStatus.TRANSFERENCIA_COMARCA]: 'Data do Desligamento por Transferência de Comarca',
      [MeasureStatus.SUBSTITUICAO_MEDIDA]: 'Data do Desligamento por Substituição de Medida',
      [MeasureStatus.IMPOSSIBILITADO_NOVA_APREENSAO]: 'Data do Desligamento por Nova Apreensão',
      [MeasureStatus.IMPOSSIBILITADO_PRISAO]: 'Data do Desligamento por Prisão',
      [MeasureStatusLaPsc.CUMPRINDO_LA_CUMPRINDO_PSC]: '',
      [MeasureStatusLaPsc.CUMPRINDO_LA_DESCUMPRINDO_PSC]:
        'Data de Cumprimento de LA, Descumprimento de PSC',
      [MeasureStatusLaPsc.DESCUMPRINDO_LA_CUMPRINDO_PSC]:
        'Data de Descumprimento de LA, Cumprimento de PSC',
      [MeasureStatusLaPsc.EXTINTA_LA_CUMPRINDO_PSC]: 'Data de Extinção de LA, Cumprimento de PSC',
      [MeasureStatusLaPsc.EXTINTA_PSC_CUMPRINDO_LA]: 'Data de Extinção de PSC, Cumprimento de LA',
      [MeasureStatusLaPsc.EXTINTA_LA_PSC]: 'Data de Extinção de LA e PSC',
      [MeasureStatusLaPsc.CUMPRINDO_LA_SUSPENSA_PSC]: 'Data de Cumprimento de LA, Suspensão de PSC',
      [MeasureStatusLaPsc.SUSPENSA_LA_CUMPRINDO_PSC]: 'Data de Suspensão de LA, Cumprimento de PSC',
      [MeasureStatusLaPsc.SUSPENSA_LA_PSC]: 'Data de Suspensão de LA e PSC',
    }

    let label = ''

    if (statusMedida) {
      label = labelMap[statusMedida as MeasureStatus | MeasureStatusLaPsc]
    }

    if (statusMedidaLaPsc && !label) {
      label = labelMap[statusMedidaLaPsc as MeasureStatus | MeasureStatusLaPsc]
    }

    return label
  }

  const getAttributeByStatus = (
    data: any,
    statusMedida?: string | null,
    statusMedidaLaPsc?: string | null,
  ) => {
    if (!data) return ''
    // Determina o tipo socioeducativo dinâmico
    const tipoSocioeducativaValue = data.tipoMedidaSocioeducativa
    const nameTipoSocioeducativa =
      tipoSocioeducativaValue === 'LA'
        ? 'la'
        : tipoSocioeducativaValue === 'PSC'
        ? 'psc'
        : tipoSocioeducativaValue === 'LA_PSC'
        ? 'laPsc'
        : ''

    // Define os mapas com base no status
    const nameMap: Record<MeasureStatus | MeasureStatusLaPsc, string> = {
      [MeasureStatus.CUMPRINDO]: 'Cumprindo',
      [MeasureStatus.DESCUMPRINDO]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataInicioDescumprimento`,
      [MeasureStatus.EXTINTA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSentencaExtincao`,
      [MeasureStatus.OBITO ||
      MeasureStatusLaPsc.OBITO]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorObito`,
      [MeasureStatus.SUSPENSA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataSentencaSuspensao`,
      [MeasureStatus.TRANSFERENCIA_CREAS ||
      MeasureStatusLaPsc.TRANSFERENCIA_CREAS]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataTransferenciaCreas`,
      [MeasureStatus.TRANSFERENCIA_COMARCA ||
      MeasureStatusLaPsc.TRANSFERENCIA_COMARCA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorTransferenciaComarca`,
      [MeasureStatus.SUBSTITUICAO_MEDIDA ||
      MeasureStatusLaPsc.SUBSTITUICAO_MEDIDA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSubstituicaoMedida`,
      [MeasureStatus.IMPOSSIBILITADO_NOVA_APREENSAO ||
      MeasureStatusLaPsc.IMPOSSIBILITADO_NOVA_APREENSAO]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorNovaApreensao`,
      [MeasureStatus.IMPOSSIBILITADO_PRISAO ||
      MeasureStatusLaPsc.IMPOSSIBILITADO_PRISAO]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorPrisao`,
      //
      [MeasureStatusLaPsc.CUMPRINDO_LA_CUMPRINDO_PSC]: 'Cumprindo LA e PSC',
      [MeasureStatusLaPsc.CUMPRINDO_LA_DESCUMPRINDO_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataInicioDescumprimentoPsc`,
      [MeasureStatusLaPsc.DESCUMPRINDO_LA_CUMPRINDO_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataInicioDescumprimentoLa`,
      [MeasureStatusLaPsc.EXTINTA_LA_CUMPRINDO_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSentencaExtincaoLa`,
      [MeasureStatusLaPsc.EXTINTA_PSC_CUMPRINDO_LA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSentencaExtincaoPsc`,
      [MeasureStatusLaPsc.EXTINTA_LA_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSentencaExtincaoLaPsc`,
      [MeasureStatusLaPsc.CUMPRINDO_LA_SUSPENSA_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataSentencaSuspensaoPsc`,
      [MeasureStatusLaPsc.SUSPENSA_LA_CUMPRINDO_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataSentencaSuspensaoLa`,
      [MeasureStatusLaPsc.SUSPENSA_LA_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataSentencaSuspensaoLaPsc`,
    }

    let attributePath = ''

    if (statusMedida) {
      attributePath = nameMap[statusMedida as MeasureStatus | MeasureStatusLaPsc]
    }

    if (statusMedidaLaPsc && !attributePath) {
      attributePath = nameMap[statusMedidaLaPsc as MeasureStatus | MeasureStatusLaPsc]
    }

    if (!attributePath) {
      throw new Error(`Status não encontrado no mapa.`)
    }

    // Navega dinamicamente no objeto para obter o valor
    const value = attributePath.split('.').reduce((acc, key) => acc?.[key], data)

    if (!value) return ''
    return formatDate(value)
  }

  const dateLabel = getDateLabel(
    dadosProcessuais?.medidaSocioeducativa.statusMedida,
    dadosProcessuais?.medidaSocioeducativa.statusMedidaLaPsc,
  )

  const dateValue = getAttributeByStatus(
    dadosProcessuais,
    dadosProcessuais?.medidaSocioeducativa.statusMedida,
    dadosProcessuais?.medidaSocioeducativa.statusMedidaLaPsc,
  )

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Typography variant={'h6'} sx={{ borderBottom: '1px solid', borderColor: '#000' }}>
        <b>DADOS PROCESSUAIS</b>
      </Typography>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Label label={'Processo de Conhecimento (VIJ):'} value={dadosProcessuais?.processoVij} />
        <Label label={'Processo de Execução (VEMSE):'} value={dadosProcessuais?.processoVemse} />
        <Label
          label={'Trata-se de uma Remissão:'}
          value={`${getRemissao(
            dadosProcessuais?.remissao?.trataRemissao,
            dadosProcessuais?.remissao?.tipoRemissao,
          )}`}
        />
      </Box>
      <Typography variant={'body1'}>
        <b>Medida aplicada:</b>
      </Typography>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Label
          label={'Medida Socioeducativa Aplicada:'}
          value={getMedidaAplicada(dadosProcessuais?.tipoMedidaSocioeducativa)}
        />
        <Label
          label={'Status da Medida:'}
          value={getStatusMedida(
            dadosProcessuais?.medidaSocioeducativa.statusMedida,
            dadosProcessuais?.medidaSocioeducativa.statusMedidaLaPsc,
          )}
        />
        {dateLabel && <Label label={`${dateLabel}:`} value={dateValue} />}
        {estadoDeclarado?.nome && (
          <Label label={'Estado de Moradia Declarado:'} value={estadoDeclarado?.nome} />
        )}

        {municipioDeclarado?.nome && (
          <Label label={'Município de Moradia Declarado:'} value={municipioDeclarado?.nome} />
        )}
        <Label
          label={'Cumprimento de Medidas Restritivas de Liberdade Anteriores:'}
          value={`${getMedidaRestritiva(dadosProcessuais?.medidaRestritiva)}`}
        />
        <Label
          label={'Reinfração (quando tiver cometido de novo ato infracional):'}
          value={`${dadosProcessuais?.reinfracao ? 'Sim' : 'Não'}`}
        />
      </Box>
    </Box>
  )
}

const MeasuresAppliedSection = ({
  medidasAplicadas,
}: {
  medidasAplicadas?: SocioeducationMeasureMeasuresAppliedType | null
}) => {
  const MeasuresApplied = ({ measuresApplied }: { measuresApplied: MeasuresAppliedType[] }) =>
    measuresApplied.length > 0 ? (
      <Box display={'flex'} flexDirection={'column'} gap={2}>
        {measuresApplied.map((measure, index) => (
          <Box key={index} display={'flex'} flexDirection={'column'} gap={1}>
            {measure.la.dataInicioCumprimento && (
              <Label
                label={'Data do Início do Cumprimento da LA:'}
                value={formatDate(measure.la.dataInicioCumprimento)}
              />
            )}
            {measure.psc.dataInicioCumprimento && (
              <Label
                label={'Data do Início do Cumprimento da PCS:'}
                value={formatDate(measure.psc.dataInicioCumprimento)}
              />
            )}

            <Label
              label={'Data da Entrega do Relatório:'}
              value={formatDate(measure.dataEntregaRelatorio)}
            />
          </Box>
        ))}
      </Box>
    ) : null

  const getPeriodicidade = (periodicidade?: string | null) => {
    if (!periodicidade) return ''

    return periodicidadeAcompanhamentoOptions.find((item) => item.value === periodicidade)?.label
  }

  const getSimNao = (simNao?: string | null) => {
    if (!simNao) return ''

    return simNaoOptions.find((item) => item.value === simNao)?.label
  }

  const getPrincipaisAtividades = (principaisAtividades?: string | null) => {
    if (!principaisAtividades) return ''
    return atividadesOptions.find((item) => item.value === principaisAtividades)?.label
  }

  const getDificuldadeAdesao = (dificuldadeAdesao?: string | null) => {
    if (!dificuldadeAdesao) return ''
    return dificuldadeAdesaoOptions.find((item) => item.value === dificuldadeAdesao)?.label
  }

  const getSugestaoExtincao = (sugestaoExtincao?: string | null) => {
    if (!sugestaoExtincao) return ''
    return sugestaoExtincaoOptions.find((item) => item.value === sugestaoExtincao)?.label
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Typography variant={'h6'} sx={{ borderBottom: '1px solid', borderColor: '#000' }}>
        <b>MEDIDAS APLICADAS</b>
      </Typography>
      <MeasuresApplied measuresApplied={medidasAplicadas?.medidasAplicadas || []} />
      <Typography variant={'subtitle2'}>
        <b>Informações sobre acompanhamento da medida Liberdade Assistida:</b>
      </Typography>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        {medidasAplicadas?.la.ativo && (
          <>
            <Label
              label={'Periodicidade do Acompanhamento:'}
              value={getPeriodicidade(medidasAplicadas?.la.periodicidadeAcompanhamento)}
            />
            <Label
              label={
                'Atividades Individuais ou Coletivas apresentadas ou desenvolvidas com o Adolescente/Jovem pela equipe de Pedagogia:'
              }
              value={medidasAplicadas?.la.atividadesIndividuaisColetivas}
              direction={'column'}
            />
          </>
        )}
        {medidasAplicadas?.psc.ativo && (
          <>
            <Label
              label={'Habilidades e Potencialidades destacadas pelo adolescente:'}
              value={medidasAplicadas?.psc.habilidadePotencialidades}
              direction={'column'}
            />
            <Label
              label={'Aguardando Articulação:'}
              value={getSimNao(medidasAplicadas.psc.aguardandoArticulacao)}
            />
            {medidasAplicadas.psc.aguardandoArticulacao === 'S' && (
              <>
                <Label
                  label={'Local do Cumprimento:'}
                  value={medidasAplicadas.psc.localCumprimento?.nome}
                  direction={'column'}
                />
                <Label
                  label={'Profissional de Referência:'}
                  value={medidasAplicadas.psc.profissionalReferencia}
                  direction={'column'}
                />
                <Label label={'Carga Horária:'} value={medidasAplicadas.psc.cargaHoraria} />
                <Label label={'Tempo Determinado:'} value={medidasAplicadas.psc.tempoDeterminado} />
                <Label
                  label={'Principais Atividades:'}
                  value={getPrincipaisAtividades(medidasAplicadas.psc.principaisAtividades)}
                  direction={'column'}
                />
              </>
            )}
            <Label
              label={'Sugestão de Extinção da PSC antes do término seu cumprimento em razão de:'}
              value={getSugestaoExtincao(medidasAplicadas?.psc.sugestaoExtincao)}
            />
            <Label
              label={'Nos casos de dificuldade de adesão – Novo Encaminhamento:'}
              value={getDificuldadeAdesao(medidasAplicadas?.psc.dificuldadeAdesao)}
            />
            <Label
              label={'Recusa do Adolescente no Cumprimento da PSC:'}
              value={getSimNao(medidasAplicadas?.psc.recusaAdolescenteCumprimento)}
            />
          </>
        )}
        <Label label={'Observações:'} value={medidasAplicadas?.observacoes} direction={'column'} />
      </Box>
    </Box>
  )
}

const AccompanimentSection = ({
  dadosAcompanhamento,
}: {
  dadosAcompanhamento?: SocioeducationAccompanimentDataType | null
}) => {
  const getSimNao = (value?: boolean | string | null) => {
    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não'
    }

    if (value === 'S') {
      return 'Sim'
    }

    if (value === 'N') {
      return 'Não'
    }

    return ''
  }

  const getMatriculado = (value?: string | null) => {
    if (value === 'S') {
      return 'Sim'
    }

    if (value === 'E') {
      return 'Evadiu'
    }

    if (value === 'C') {
      return 'Concluiu'
    }

    return ''
  }

  const getTrabalho = (value?: string | null) => {
    if (!value) return ''
    return trabalhoOptions.find((item) => item.value === value)?.label
  }

  const getFamiliaAcompanhaOutroServico = (value?: string | null) => {
    if (!value) return ''
    return familiaAcompanhaOutroServicoOptions.find((item) => item.value === value)?.label
  }

  const getCultural = (value?: string | null) => {
    if (!value) return ''
    return culturalOptions.find((item) => item.value === value)?.label
  }

  const getEsportes = (value?: string | null) => {
    if (!value) return ''
    return sportsOptions.find((item) => item.value === value)?.label
  }

  const getMotivos = (value?: string | null) => {
    if (!value) return ''
    return motivosOptions.find((item) => item.value === value)?.label
  }

  const getMotivosNaoAdesao = (value?: string | null) => {
    if (!value) return ''
    return motivosNaoAdesaoOptions.find((item) => item.value === value)?.label
  }

  const getSubstancias = (item: any): string => {
    if (!item) return ''
    const substancesArray = substances
      .filter((substance) => item[substance.name] === 'S')
      .map((substance) => substance.label)

    const lastItem = substancesArray.pop()

    if (substancesArray.length > 0) {
      return [substancesArray.join(', '), lastItem ? lastItem : ''].join(' e ')
    }

    return formatText(lastItem)
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Typography variant={'h6'} sx={{ borderBottom: '1px solid', borderColor: '#000' }}>
        <b>ACOMPANHAMENTO</b>
      </Typography>
      <Typography variant={'body1'}>
        <b>
          Informações sobre escolarização e processo de aprendizagem, profissionalização e trabalho:
        </b>
      </Typography>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Typography variant={'body1'}>
          <b>Escolarização</b>
        </Typography>
        <Box display={'flex'} flexDirection={'column'} gap={1} ml={2}>
          <Label
            label={'Matriculado:'}
            value={getMatriculado(
              dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.matriculado,
            )}
          />
          {dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.matriculado === 'S' && (
            <>
              <Label
                label={'Unidade de Ensino:'}
                value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.unidadeEnsino}
              />
              <Label
                label={'Ano Escolar:'}
                value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.anoEscolar}
              />
              <Label
                label={'Turno:'}
                value={
                  dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.turnoEscolarizacao
                }
              />
              <Label
                label={'Frequente:'}
                value={getSimNao(
                  dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.frequencia,
                )}
              />
            </>
          )}

          {dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.matriculado === 'E' && (
            <Label
              label={'Em qual ano parou:'}
              value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.anoParou}
            />
          )}

          {dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.matriculado === 'C' && (
            <Label
              label={'Ano de conclusão:'}
              value={
                dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.anoConclusaoEstudo
              }
            />
          )}
          <Label
            label={'Vínculo Escolar e Desenvolvimento:'}
            value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.vinculoEscolar}
          />
        </Box>
        <Typography variant={'body1'}>
          <b>Jovem Aprendiz</b>
        </Typography>
        <Box display={'flex'} flexDirection={'column'} gap={1} ml={2}>
          <Label
            label={'Inserido no Jovem Aprendiz:'}
            value={getSimNao(
              dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.jovemAprendiz,
            )}
          />
          {isSimNao(dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.jovemAprendiz) ? (
            <>
              <Label
                label={'Data de Início:'}
                value={formatDate(
                  dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.dataInicio,
                )}
              />
              <Label
                label={'Empresa:'}
                value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.empresa}
              />
              <Label
                label={'Contato:'}
                value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.contato}
              />
              <Label
                label={'Turno:'}
                value={
                  dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.turnoJovemAprendiz
                }
              />
            </>
          ) : (
            <>
              <Label
                label={'Motivo:'}
                value={getMotivos(
                  dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.motivoNaoInserido,
                )}
              />
              <Label
                label={'Não Adesão:'}
                value={getMotivosNaoAdesao(
                  dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.motivoNaoAdesao,
                )}
              />
            </>
          )}
          <Label
            label={'Apresenta Interesse:'}
            value={getSimNao(
              dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.apresentaAreaInteresse,
            )}
          />
          {isSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.apresentaAreaInteresse,
          ) && (
            <>
              <Label
                label={'Qual o Objetivo:'}
                value={
                  dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho
                    ?.objetivoAreaInteresse
                }
              />
            </>
          )}
          <Label
            label={'Oportunidades de Jovem Aprendiz Encaminhadas:'}
            value={
              dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho
                ?.oportunidadesJovemAprendizEcaminhadas
            }
            direction={'column'}
          />
        </Box>
        <Typography variant={'body1'}>
          <b>Trabalho</b>
        </Typography>
        <Box display={'flex'} flexDirection={'column'} gap={1} ml={2}>
          <Label
            label={'Trabalho:'}
            value={getTrabalho(
              dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.trabalho,
            )}
          />
          <Label
            label={'Atividade:'}
            value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.ativadades}
          />
          <Label
            label={'Local:'}
            value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.local}
          />
          <Label
            label={'Turno:'}
            value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.turnoTrabalho}
          />
          <Label
            label={'Remuneração:'}
            value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.renda?.descricao}
          />
          <Label
            label={'Observações:'}
            value={dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.observacao}
            direction={'column'}
          />
          <Label
            label={'Adolescente ou Jovem egresso de trabalho infantil:'}
            direction={'column'}
            value={getSimNaoDescricao(
              dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho
                ?.engressoTrabalhoInfantil,
              dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho
                ?.atividadeTrabalhoInfantil,
            )}
          />
        </Box>
        <Typography variant={'body1'}>
          <b>Profissionalização</b>
        </Typography>
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          <Typography variant={'subtitle2'}>
            <b>Encaminhado a Curso Profissionalizante ou Projetos de Inclusão Produtivo:</b>
          </Typography>
          <Typography variant={'body2'}>
            {getSimNaoDescricao(
              dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.profissionalizacao,
              dadosAcompanhamento?.meioAbertoAcompInfoEscolaAprendTrabalho?.cursoProfissionalizante,
            )}
          </Typography>
        </Box>
      </Box>
      <Typography variant={'body1'}>
        <b>Informações Sobre Saúde Integral:</b>
      </Typography>
      <Box display={'flex'} flexDirection={'column'} gap={1} ml={2}>
        <Label
          label={'Adolescente/Jovem chegou ao serviço vinculado a alguma Unidade de Saúde:'}
          value={getSimNaoDescricao(
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.vinculadoUnidadeSaude,
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.unidadeSaude,
          )}
          direction={'column'}
        />
        {dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.vinculadoUnidadeSaude !== null &&
          !isSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.vinculadoUnidadeSaude,
          ) && (
            <>
              <Box display={'flex'} flexDirection={'column'} gap={1}>
                <Typography variant={'body2'}>
                  <b>
                    Caso não, qual Clínica da Família de referência para atendimento pelo PNAISARI:
                  </b>
                </Typography>
                <Typography variant={'body2'}>
                  {
                    dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral
                      ?.clinicaDeFamiliaDeReferencia
                  }
                </Typography>
              </Box>
            </>
          )}
        <Label
          label={'Possui Plano de Saúde Particular:'}
          value={getSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.possuiPlanoParticular,
          )}
        />
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          <Typography variant={'subtitle2'}>
            <b>
              Projeto Terapêutico Singular ou informações de saúde compartilhadas pela Unidade de
              Referência:
            </b>
          </Typography>
          <Typography variant={'body2'}>
            {getSimNao(
              dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral
                ?.projetoTerapeuticoSingularOuSaudeCompUnSaude,
            )}
          </Typography>
        </Box>
        <Label
          label={'Possui alguma doença crônica:'}
          value={getSimNaoDescricao(
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.possuiDoencaCronica,
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.doencasCronicas,
          )}
        />
        <Label
          label={'Possui deficiência:'}
          value={getSimNaoDescricao(
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.possuiDeficiencia,
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.deficiencia,
          )}
        />
        <Label
          label={'Acompanhamento em Saúde Mental:'}
          value={getSimNaoDescricao(
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.possuiAcompanhamentoSaudeMental,
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.acompanhamentoSaudeMental,
          )}
        />
        {isSimNao(
          dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.possuiAcompanhamentoSaudeMental,
        ) && (
          <Label
            label={'Unidade:'}
            value={dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.unidadeSaudeMental}
          />
        )}
        <Label
          label={'Faz uso de Drogas Lícitas e Ilícitas:'}
          value={getSimNaoDescricao(
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.fazUsoDrogasLicitasIlicitas,
            getSubstancias(dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.substancias),
          )}
        />
        {isSimNao(
          dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.fazUsoDrogasLicitasIlicitas,
        ) && (
          <>
            <Label
              label={'Qual a relação o adolescente ou jovem considera ter com as drogas:'}
              value={dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.relacaoAdolescenteDroga}
              direction={'column'}
            />
            <Label
              label={
                'Como os responsáveis acompanham e avaliam o uso de droga pelo adolescente/jovem:'
              }
              value={
                dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.avaliacaoResponsaveisUsoDroga
              }
              direction={'column'}
            />
          </>
        )}
        <Label
          label={'Possui laudo médico de transtorno mental ou deficiência intelectual:'}
          value={getSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral
              ?.possuiLaudoDeficienciaIntelectual,
          )}
        />
        <Label
          label={
            'Transtorno mental ou deficiência intelectual que enseje a revisão de medida aplicada:'
          }
          value={getSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral
              ?.revisaoMedicaTranstornoOuDeficienciaIntelectual,
          )}
        />
        <Label
          label={'Observações:'}
          value={dadosAcompanhamento?.meioAbertoAcompInfoSaudeIntegral?.obsevacoes}
          direction={'column'}
        />
      </Box>
      <Typography variant={'body1'}>
        <b>Informações Sobre Cultura e Esporte:</b>
      </Typography>
      <Box display={'flex'} flexDirection={'column'} gap={1} ml={2}>
        <Label
          label={'Área de Interesse Cultural:'}
          value={getCultural(
            dadosAcompanhamento?.meioAbertoAcompInfoCulturaEsporte?.areaInteresseCultura,
          )}
          direction={'column'}
        />
        <Label
          label={'Possui ID JOVEM para acesso as atividades culturais:'}
          value={getSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoCulturaEsporte
              ?.possuiIdJovemParaAtividadesCulturais,
          )}
        />
        <Label
          label={'Área de Interesse Esporte:'}
          value={getEsportes(
            dadosAcompanhamento?.meioAbertoAcompInfoCulturaEsporte?.areaInteresseEsporte,
          )}
          direction={'column'}
        />
        <Label
          label={'Observações:'}
          value={dadosAcompanhamento?.meioAbertoAcompInfoCulturaEsporte?.observacoes}
          direction={'column'}
        />
      </Box>
      <Typography variant={'body1'}>
        <b>Informações Sobre Rede Socioassistencial:</b>
      </Typography>
      <Box display={'flex'} flexDirection={'column'} gap={1} ml={2}>
        <Label
          label={'CRAS de Referência:'}
          value={dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial?.crasReferencia}
          direction={'column'}
        />
        <Label
          label={'Participação no Serviço de Convivência e Fortalecimento de Vínculos:'}
          value={getSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial
              ?.participaServConvFortaleciementoVinculo,
          )}
        />
        <Label
          label={'Participação em projetos sociais de Organizações da Sociedade Civil:'}
          value={getSimNaoDescricao(
            dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial?.participaProjetosSociais,
            dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial?.atividadeProjetoSocial,
          )}
        />
        <Label
          label={'Família acompanhada em algum outro serviço:'}
          value={getFamiliaAcompanhaOutroServico(
            dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial
              ?.familiaAcompanhaOutroServico,
          )}
          direction={'column'}
        />
        <Label
          label={'Família inserida no Cad Único:'}
          value={getSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial?.familiaInseridaCadUnico,
          )}
        />
        <Label
          label={'Família beneficiária do Programa de Transferência de Renda do Governo Federal:'}
          value={getSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial
              ?.familiaBeneProgTransfRendaFederal,
          )}
        />
        <Label
          label={'Adolescente/Jovem beneficiário do BPC:'}
          value={getSimNao(
            dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial?.adolescenteBeneBPC,
          )}
        />
        <Label
          label={'Família atendida em outros programas ou ações de benefícios sociais:'}
          value={getSimNaoDescricao(
            dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial
              ?.familiaAtendidaOutroProgSociais,
            dadosAcompanhamento?.meioAbertoAcompInfoRedeSocioassistencial
              ?.programaOuBeneficiosSociais,
          )}
        />
      </Box>
    </Box>
  )
}
